/* Ordering products by PUBLISHED date.

   Shopify cannot do this server-side, and the gap is structural rather than a
   theme shortcoming: `sort_by` accepts a fixed list (manual / best-selling /
   title-* / price-* / created-*) with no published value; automated
   collections have no date CONDITION; and Liquid cannot sort across
   pagination, because each page's membership is decided before the theme sees
   it. Measured on /collections/allshoes: the four most recently published
   products sit at positions 293-296 of 307 in the collection's own order, so
   "render the first N and sort them" cannot reach them either.

   So the ORDER is computed here from the public products.json feed, while the
   CARDS are still rendered by the theme - harvested a page at a time from the
   collection's own grid section, which is what lets this cover a whole
   collection rather than a fixed two dozen (see "Where the cards come from").
   Swatches, badges, the size flyout and the add-to-cart path stay identical to
   every other grid instead of being reimplemented in JS.

   The grid keeps loading as the shopper scrolls. It used to stop dead at a
   configured count - reported as "it shows too little of products" - because
   the takeover has to disable the theme's own infinite scroll, which would
   otherwise append the SERVER's ordering underneath this one.

   Two entry points share all of it:
     <new-arrivals-grid>  a standalone section on its own page
     <published-sort>     takes over an existing collection grid when the URL
                          carries the agreed parameter

   Liquid cannot read arbitrary query params (see the note in
   sections/card-product-swap.liquid), which is why the parameter is read here
   and not in the section. */

/* Wrapped in an IIFE so a second inclusion on the same page is harmless. Both
   entry points below already guard against re-defining their custom element,
   but a top-level `const NewArrivals` would throw "already been declared"
   before either guard ran, taking the whole script down. The collection
   section and the standalone page section both load this file, and a single
   page could end up with both. */
(function () {
  const NewArrivals = {
  /* Carry the current page's market context onto every fetch, exactly as
     card-interactions.js does for the colour swap. A plain fetch() does not
     inherit whatever market resolution the page's navigation used, and
     landing in a different market makes variants resolve unavailable, which
     silently empties the size flyout. No-op when the URL carries neither
     param, which is the common case. */
  marketSuffix() {
    const current = new URLSearchParams(window.location.search);
    const forwarded = new URLSearchParams();
    ['country', 'locale'].forEach((key) => {
      const value = current.get(key);
      if (value) forwarded.set(key, value);
    });
    const query = forwarded.toString();
    return query ? `&${query}` : '';
  },

  /* /collections/<handle>, and also /collections/<handle>/<tag> when a tag
     filter is applied - the handle is always the segment after "collections".
     Read from the PATH rather than passed from Liquid, so one script serves
     every collection without the section knowing which it is on. */
  collectionHandleFromPath() {
    const parts = window.location.pathname.split('/').filter(Boolean);
    const index = parts.indexOf('collections');
    return index !== -1 && parts[index + 1] ? decodeURIComponent(parts[index + 1]) : null;
  },

  /* When did this product actually go on sale?

     Shopify records no such date, and neither field that looks like one works
     on this store:

       published_at  is "when it was last published", not "when it first went
                     live". A sold-out item that is restocked gets re-published
                     and the date resets to today, so a year-old product lands
                     at the top of a page whose whole promise is "newest
                     first". Measured on /collections/allsitecloths: the top
                     NINE all shared a published_at of 2026-09-17 11:58 - one
                     bulk action - having been created 315 days earlier on
                     2025-11-05. Nine of the visible top twelve carried
                     photography over 120 days old. This is the reported bug.

       created_at    is too early, because products here are created in advance
                     and published later. 42 products were created more than 90
                     days before going on sale - maxine-black-mules was created
                     2025-11-05 and launched 2026-09-16 - so ordering by it
                     buries genuinely new arrivals.

     The photographs do track it. A product is shot shortly before it goes on
     sale, and uploading an image is not something re-publishing touches, so
     the earliest photo separates the two cases that the dates above confuse:

       created in advance, shot recently  ->  photo is recent   -> ranks high
       old stock, restocked, re-published ->  photo is old      -> ranks low

     Measured across the catalogue: of 319 products published more than 90 days
     after creation, 233 were photographed more than 90 days before publishing
     (old stock) and 42 within 30 days of it (genuinely new). Only that split
     tells them apart.

     `max(created_at, photo)` would be equivalent - an image cannot predate the
     product it belongs to - so the earliest photo is taken directly, with
     created_at as the fallback for a product that has none.

     Known limit: re-shooting every photograph on an old product moves it up,
     the way re-publishing does today. Replacing SOME photos does not, since
     this takes the earliest. Across the whole catalogue only 38 products have
     a first photo dated after their publish date at all. */
  launchedAt(product) {
    let earliest = Infinity;
    (product.images || []).forEach((image) => {
      const taken = Date.parse(image.created_at);
      if (!Number.isNaN(taken) && taken < earliest) earliest = taken;
    });
    return earliest === Infinity ? Date.parse(product.created_at) : earliest;
  },

  /* products.json caps at 250 per page. Bounded at 8 pages (2000 products) so
     a misconfiguration cannot spin forever. */
  async fetchFeed(handle) {
    const all = [];
    for (let page = 1; page <= 8; page += 1) {
      const url = `/collections/${encodeURIComponent(handle)}/products.json?limit=250&page=${page}`;
      const response = await fetch(url);
      if (!response.ok) break;
      const data = await response.json();
      const products = data.products || [];
      all.push(...products);
      if (products.length < 250) break;
    }
    return all;
  },

  /* --- Where the cards come from -------------------------------------------

     Cards used to be fetched one at a time, through
     /products/<handle>?section_id=card-product-swap. That is fine for a fixed
     two dozen and cannot carry a whole collection, which is what this view now
     has to do. Measured against the storefront:

       8 requests/sec  ->  429 from request 72
       4 requests/sec  ->  429 from request 52
       2 requests/sec  ->  80 requests, no 429 at all

     So roughly two a second is the ceiling, and one-per-card would need 312 of
     them for /collections/allshoes - twenty-plus minutes of paced fetching,
     or throttling and silently losing products, which is what a first attempt
     did.

     A page of the collection's OWN grid section carries sixteen cards in one
     request, and compresses to 23 KB against 4.7 KB for a single card - three
     times cheaper per card in bytes and sixteen times cheaper in requests.
     Modelled over the real collection: 3 requests for the first screenful, 21
     for all 312 products.

     What makes it work is that /collections/<handle>/products.json returns
     products in the COLLECTION's own order - verified: section page 1 holds
     feed entries 0-15, page 2 holds 16-31. So a product's position in the feed
     says which section page renders it, and the published-date ordering can be
     served by fetching only the pages it actually needs. */

  /* The section's page size is not knowable from here - it is a theme setting
     - but it is recoverable: `paginate` splits the collection into
     ceil(total / size) pages, so size = ceil(total / pages), and the page
     count is in the pagination markup. */
  lastPageNumber(doc) {
    let last = 1;
    doc.querySelectorAll('a[href*="page="]').forEach((link) => {
      const match = /[?&]page=(\d+)/.exec(link.getAttribute('href') || '');
      if (match) last = Math.max(last, parseInt(match[1], 10));
    });
    return last;
  },

  async fetchSectionPage(handle, page, attempt = 0) {
    const url = `/collections/${encodeURIComponent(
      handle
    )}?section_id=main-collection-product-grid&page=${page}${this.marketSuffix()}`;
    const response = await fetch(url);

    /* Paced fetching should keep us clear of the limit, but a shopper does not
       browse in isolation - apps and pixels are making requests too. A
       throttled page waits and asks again rather than costing sixteen products
       that would then be missing from the grid with nothing to show for it. */
    if ((response.status === 429 || response.status >= 500) && attempt < 2) {
      const header = parseFloat(response.headers.get('Retry-After'));
      const wait = Number.isFinite(header)
        ? Math.min(header * 1000, 5000)
        : 800 * Math.pow(3, attempt);
      await new Promise((resolve) => setTimeout(resolve, wait));
      return this.fetchSectionPage(handle, page, attempt + 1);
    }

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  },

  /* Pulls every card out of a fetched page and files it by product handle.

     The list item is REBUILT rather than reused as-is, and the class is the
     reason: with "reveal on scroll" enabled the theme renders each item as
     `grid__item scroll-trigger animate--slide-in`, which starts at zero
     opacity and is revealed by an observer that binds once, at page load. A
     card inserted afterwards carrying those classes would never be observed
     and would stay invisible forever. Assigning `itemClass` drops them.

     `itemClass` also differs by caller and is not cosmetic: the collection
     grid is styled for `grid__item`, whose width base.css sets with
     `calc(25% - ...)` for a FLEX grid; the standalone page uses its own CSS
     grid tracks, where that width would fight the tracks - the mismatch that
     wrapped related products onto a second row earlier in this branch. */
  harvestCards(doc, cache, itemClass) {
    doc.querySelectorAll('#product-grid > li').forEach((source) => {
      const link = source.querySelector('a[href*="/products/"]');
      if (!link) return;
      const path = (link.getAttribute('href') || '').split('/products/')[1];
      if (!path) return;
      const handle = decodeURIComponent(path.split(/[?#]/)[0]);
      if (!handle || cache.has(handle)) return;

      /* importNode, not appendChild of the foreign node: this leaves the
         parsed page free to be collected instead of being kept alive by the
         handful of nodes we are holding on to. The item stays DETACHED until
         it is rendered, so its lazy images cost nothing while it waits. */
      const item = document.createElement('li');
      item.className = itemClass;
      Array.from(source.childNodes).forEach((child) => {
        item.appendChild(document.importNode(child, true));
      });
      this.absorbAssets(item);
      cache.set(handle, item);
    });
  },

  /* card-product-var.liquid emits its stylesheet <link> tags inside the first
     card of a grid, so a harvested page brings copies along. Removing a link
     removes its stylesheet from the document, and keeping duplicates inside
     every batch of cards would pile up - so the first copy is moved to <head>
     where nothing will clear it, and later ones are dropped. */
  absorbAssets(item) {
    item.querySelectorAll('link[rel="stylesheet"], style').forEach((node) => {
      const href = node.getAttribute && node.getAttribute('href');
      if (href) {
        if (!this.adoptedAssets.has(href)) {
          this.adoptedAssets.add(href);
          document.head.appendChild(node);
          return;
        }
      } else if (!this.adoptedInlineStyles) {
        this.adoptedInlineStyles = true;
        document.head.appendChild(node);
        return;
      }
      node.remove();
    });
  },

  adoptedAssets: new Set(),
  adoptedInlineStyles: false,

  /* Mirrors card-interactions.js's own refreshWishlist - two possible global
     entry points depending on which version of the app is installed, and a
     swallowed error because a wishlist that fails to rebind must not take the
     grid down with it. */
  refreshWishlist() {
    try {
      if (typeof window.WishlistHero?.reloadWishlistHeroButtons === 'function') {
        window.WishlistHero.reloadWishlistHeroButtons();
      } else if (typeof window.WishlistHeroReloadButtons === 'function') {
        window.WishlistHeroReloadButtons();
      }
    } catch (error) {
      /* Non-fatal by design. */
    }
  },

  /* Spinner plus text, as a live region so a screen reader announces the wait
     rather than sitting in silence through what can be a slow build. The ring
     itself is aria-hidden - it carries no information the text does not. */
  buildStatus(loadingText, errorText) {
    const status = document.createElement('div');
    status.className = 'new-arrivals__status';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.dataset.errorText = errorText || '';

    const spinner = document.createElement('span');
    spinner.className = 'new-arrivals__spinner';
    spinner.setAttribute('aria-hidden', 'true');

    const text = document.createElement('span');
    text.dataset.statusText = '';
    text.textContent = loadingText || '';

    status.append(spinner, text);
    return status;
  },

  /* Card-shaped placeholders, so the grid has its final layout from the first
     frame instead of a blank area that fills in later. The media box carries
     the same 3:4 ratio the real cards use, which is what stops the page
     jumping as each skeleton is swapped for a card.

     aria-hidden and inert to assistive tech - the live region on the status
     pill is what announces progress; a screen reader reading out two dozen
     empty boxes would be noise. */
  buildSkeleton(itemClass) {
    const item = document.createElement('li');
    item.className = `${itemClass} new-arrivals__skeleton`;
    item.setAttribute('aria-hidden', 'true');
    item.innerHTML =
      '<div class="new-arrivals__skeleton-media"></div>' +
      '<div class="new-arrivals__skeleton-line new-arrivals__skeleton-line--title"></div>' +
      '<div class="new-arrivals__skeleton-line new-arrivals__skeleton-line--price"></div>';
    return item;
  },

  renderSkeletons(grid, count, itemClass) {
    const skeletons = [];
    const fragment = document.createDocumentFragment();
    for (let i = 0; i < count; i += 1) {
      const skeleton = this.buildSkeleton(itemClass);
      skeletons.push(skeleton);
      fragment.appendChild(skeleton);
    }
    grid.appendChild(fragment);
    return skeletons;
  },

  /* Errors replace the TEXT and drop the spinner - a ring that keeps turning
     after a failure claims work is still happening. */
  failStatus(status) {
    if (!status) return;
    status.hidden = false;
    status.querySelector('.new-arrivals__spinner')?.remove();
    const text = status.querySelector('[data-status-text]');
    const message = status.dataset.errorText;
    if (text && message) text.textContent = message;
  },

  /* An invisible marker placed after the grid. When it comes near the viewport
     the next page of cards is rendered - the published-date view's equivalent
     of the collection grid's Ajaxinate, which the takeover has to remove (see
     PublishedSort.takeOver) because Ajaxinate would append the SERVER's
     ordering underneath our own.

     `afterend` rather than inside the <ul>: the collection grid is a flex
     container and the standalone page a CSS grid, so a child would be laid out
     as a cell and leave a hole in the last row. */
  buildSentinel() {
    const sentinel = document.createElement('div');
    sentinel.className = 'new-arrivals__sentinel';
    sentinel.setAttribute('aria-hidden', 'true');
    /* One of the store's app stylesheets carries
       `div:empty { display: none }`, which matches this element exactly. A
       display:none element has a zero rect and never intersects anything, so
       the marker was invisible to the observer AND measured as sitting at the
       top of the viewport - which made the "did this page fill the screen"
       check below always true and loaded seven pages before the shopper had
       scrolled once. An inline declaration outranks it without needing to know
       the rule is there. */
    sentinel.style.setProperty('display', 'block', 'important');
    return sentinel;
  },

  async populate({ grid, status, handle, pageSize, hideSoldOut, itemClass }) {
    if (!grid || !handle) return;

    /* Move the status to <body> before anything else.

       It is `position: fixed`, which anchors to the VIEWPORT only while no
       ancestor is transformed - a transformed ancestor becomes the containing
       block instead. On the collection page `.product-grid-container` carries
       `scroll-trigger animate--slide-in`, which is transform-based, so the
       pill was being pinned to the bottom of that container rather than the
       screen and sat far below the fold on a tall grid: spinning invisibly,
       reported as a white page.

       Re-parenting to <body> puts it outside every transformed wrapper, so
       `fixed` means fixed. Cheap, and it makes the indicator independent of
       whatever markup either entry point happens to sit inside. */
    if (status && status.parentElement !== document.body) {
      document.body.appendChild(status);
    }

    /* Placeholders go in BEFORE the feed is fetched - that request alone can
       take a second, and this is what the shopper looks at meanwhile. */
    /* Placeholders for the FIRST page only. Everything past it is rendered on
       demand as the shopper scrolls, so the page is never asked to lay out
       three hundred boxes at once. */
    const firstSkeletons = this.renderSkeletons(grid, pageSize, itemClass);

    /* Safe to reveal the grid now: it holds placeholders rather than the
       server's unsorted products, which is what the pending flag was hiding.
       Harmless on the standalone page, where the flag is never set. */
    document.documentElement.classList.remove('published-sort-pending');

    let products;
    try {
      products = await this.fetchFeed(handle);
    } catch (error) {
      console.error('new-arrivals: could not read the collection feed', error);
      /* Leaving placeholders behind after a failure would imply cards are
         still on their way. */
      firstSkeletons.forEach((skeleton) => skeleton.remove());
      this.failStatus(status);
      return;
    }

    /* The feed is kept in the collection's OWN order as well as sorted, and
       both are needed: the sorted list is what the shopper sees, and the
       original positions are what say which section page renders each card.
       Sold-out products are counted here even when they are not shown, because
       `paginate` counts them too - the grid skips them inside the loop, after
       the page boundaries have already been decided. */
    const position = new Map();
    products.forEach((product, index) => position.set(product.handle, index));
    const total = products.length;

    /* Matches main-collection-product-grid.liquid, which skips products with
       no available variant, so the two views cannot disagree about whether a
       sold-out product exists. */
    let order = hideSoldOut
      ? products.filter((product) => (product.variants || []).some((variant) => variant.available))
      : products.slice();

    order.sort((a, b) => this.launchedAt(b) - this.launchedAt(a));

    if (!order.length) {
      firstSkeletons.forEach((skeleton) => skeleton.remove());
      this.failStatus(status);
      return;
    }

    /* --- Fetching the cards ------------------------------------------------

       One request brings back a whole page of the collection's grid, so cards
       are collected by the page and drawn from here as the ordering asks for
       them. See the note above harvestCards for the measurements.

       The first page is fetched unconditionally: it is where the page COUNT
       is read from, which is the only way to work out how many products the
       server puts on a page. */
    const cards = new Map();
    const fetchedPages = new Set();
    let serverPageSize = 0;

    try {
      const first = await this.fetchSectionPage(handle, 1);
      fetchedPages.add(1);
      this.harvestCards(first, cards, itemClass);
      serverPageSize = Math.max(1, Math.ceil(total / this.lastPageNumber(first)));
    } catch (error) {
      console.error('new-arrivals: could not read the collection grid', error);
      firstSkeletons.forEach((skeleton) => skeleton.remove());
      this.failStatus(status);
      return;
    }

    /* Fetches whatever section pages hold the products about to be rendered
       and are not already in hand. Two at a time: measured, the storefront
       answers 429 above roughly two requests a second, and a shopper's browser
       is making other requests at the same time. In practice this is 3 pages
       for the first screenful and 1-2 per screen after that. */
    const collectCards = async (wanted) => {
      const needed = [];
      wanted.forEach((product) => {
        if (cards.has(product.handle)) return;
        const index = position.get(product.handle);
        if (index === undefined) return;
        const page = Math.floor(index / serverPageSize) + 1;
        if (!fetchedPages.has(page) && needed.indexOf(page) === -1) needed.push(page);
      });

      for (let i = 0; i < needed.length; i += 2) {
        const wave = needed.slice(i, i + 2);
        await Promise.all(
          wave.map(async (page) => {
            fetchedPages.add(page);
            try {
              this.harvestCards(await this.fetchSectionPage(handle, page), cards, itemClass);
            } catch (error) {
              /* One page short is some missing cards, not a broken grid - the
                 products it held are skipped and the rest still render. */
              console.error(`new-arrivals: collection page ${page} failed`, error);
            }
          })
        );
      }
    };

    /* --- Progressive rendering ---------------------------------------------

       The ORDER is settled above, over the whole collection, so paging is a
       pure slice of a list already in hand - no further feed requests, and no
       possibility of a later page arriving out of order. */
    let cursor = 0;
    let loading = false;
    let sentinel = null;
    let observer = null;

    const finish = () => {
      if (observer) observer.disconnect();
      observer = null;
      if (sentinel) sentinel.remove();
      sentinel = null;
      if (status && status.isConnected) status.remove();
    };

    /* Renders one page into the placeholders it is handed. Returns how many
       real cards landed, so an entirely failed page can stop the loop instead
       of scrolling forever against a dead endpoint. */
    const renderPage = async (placeholders) => {
      const slice = order.slice(cursor, cursor + placeholders.length);
      cursor += slice.length;

      /* Fewer products than placeholders - drop the surplus now rather than
         leaving empty boxes shimmering under the real cards. */
      placeholders.slice(slice.length).forEach((skeleton) => skeleton.remove());

      await collectCards(slice);

      let rendered = 0;
      slice.forEach((product, i) => {
        /* Swap each card into its OWN placeholder's position rather than
           appending, so nothing already on screen moves. */
        const card = cards.get(product.handle);
        const skeleton = placeholders[i];
        if (card) {
          /* Dropped from the cache once placed: the node now lives in the
             document, and holding a second reference would keep every card
             ever rendered alive for the life of the page. */
          cards.delete(product.handle);
          if (skeleton && skeleton.isConnected) skeleton.replaceWith(card);
          else grid.appendChild(card);
          rendered += 1;
        } else if (skeleton) {
          /* Its page failed to load - take the placeholder away rather than
             leaving one shimmering forever. */
          skeleton.remove();
        }
      });

      /* The wishlist heart is app-injected markup: WishlistHero binds its
         buttons once, at page load, so cards added afterwards carry the
         markup but no working control. The colour swap calls the same thing
         for the same reason. */
      this.refreshWishlist();
      return rendered;
    };

    const loadMore = async () => {
      if (loading || cursor >= order.length) return;
      loading = true;
      /* The pill is the "loading more" indicator for every page after the
         first, alongside the skeletons themselves. Hidden between pages so it
         does not sit there implying work that is not happening. */
      if (status) status.hidden = false;

      const remaining = order.length - cursor;
      const placeholders =
        cursor === 0
          ? firstSkeletons
          : this.renderSkeletons(grid, Math.min(pageSize, remaining), itemClass);

      let rendered = 0;
      try {
        rendered = await renderPage(placeholders);
      } catch (error) {
        console.error('new-arrivals: could not build the grid', error);
        placeholders.forEach((skeleton) => skeleton.isConnected && skeleton.remove());
        if (observer) observer.disconnect();
        if (sentinel) sentinel.remove();
        this.failStatus(status);
        loading = false;
        return;
      }
      loading = false;

      if (!rendered || cursor >= order.length) {
        finish();
        return;
      }
      if (status) status.hidden = true;

      ensureSentinel();

      /* A page that does not fill the viewport leaves the marker on screen
         with no intersection CHANGE left to fire on, and the grid would stall
         a screen short of the fold.

         Re-observing is how that is asked without measuring anything:
         observe() always delivers an initial callback carrying the CURRENT
         state, so if the marker is still in view another page starts, and if
         it is not, nothing happens until the shopper scrolls. Reading a rect
         here instead is what went wrong first time round - see buildSentinel
         for the stylesheet that made the measurement lie. */
      if (observer && sentinel) {
        observer.unobserve(sentinel);
        observer.observe(sentinel);
      }
    };

    const ensureSentinel = () => {
      /* No IntersectionObserver - the grid stays at its first page, which is
         exactly how this view behaved before. Nothing breaks, there is just
         less of it. */
      if (!('IntersectionObserver' in window) || sentinel) return;
      sentinel = this.buildSentinel();
      grid.insertAdjacentElement('afterend', sentinel);
      /* 600px of lead time, so the next page is usually already in place by
         the time the shopper reaches the end of the current one. */
      observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) loadMore();
        },
        { rootMargin: '600px 0px' }
      );
      observer.observe(sentinel);
    };

    await loadMore();
  },
  };

  /* --- Standalone section, on its own page ---------------------------------- */

  if (!customElements.get('new-arrivals-grid')) {
  customElements.define(
    'new-arrivals-grid',
    class NewArrivalsGrid extends HTMLElement {
      connectedCallback() {
        /* The Section Rendering API re-inserts elements (theme editor, or any
           section re-render) and connectedCallback fires again each time.
           Without this the whole fetch storm would run twice and append a
           duplicate set of cards. */
        if (this.dataset.built === 'true') return;
        this.dataset.built = 'true';

        NewArrivals.populate({
          grid: this.querySelector('[data-grid]'),
          status: this.querySelector('[data-status]'),
          handle: this.dataset.collection || 'all',
          /* Products per LOAD, not in total - the grid keeps going as the
             shopper scrolls. */
          pageSize: parseInt(this.dataset.count, 10) || 24,
          hideSoldOut: this.dataset.hideSoldOut === 'true',
          itemClass: 'new-arrivals__item',
        });
      }
    }
  );
  }

  /* --- Takeover of an existing collection grid ------------------------------ */

  if (!customElements.get('published-sort')) {
  customElements.define(
    'published-sort',
    class PublishedSort extends HTMLElement {
      connectedCallback() {
        if (this.dataset.built === 'true') return;

        const params = new URLSearchParams(window.location.search);
        const key = this.dataset.param || 'sort_by';
        const value = this.dataset.value || 'published-descending';

        /* Absent the parameter this element does nothing at all - the
           server-rendered collection is left exactly as it was, infinite
           scroll included. Shopify ignores the unknown `sort_by` value rather
           than erroring on it (verified: 200, 12 cards), so the page under us
           is a normal, valid collection page. */
        if (params.get(key) !== value) return;
        this.dataset.built = 'true';
        this.takeOver();
      }

      async takeOver() {
        const grid = document.querySelector(this.dataset.gridSelector || '#product-grid');
        const handle = NewArrivals.collectionHandleFromPath();

        /* Bailing out must ALSO reveal the grid. The inline script hid it
           before this ran, so an early return that skipped this would leave
           the collection permanently blank - the failure mode being guarded
           against, reached by the guard itself. */
        if (!grid || !handle) {
          document.documentElement.classList.remove('published-sort-pending');
          return;
        }

        /* Infinite scroll MUST be stopped first. main-collection-product-grid
           .liquid initialises Ajaxinate against '#product-grid' with
           '.infinite_next' as its trigger, and it would happily append page 2,
           3, ... of the SERVER's ordering underneath our published-date
           ordering - producing a grid that is sorted at the top and not at the
           bottom, which is worse than not sorting at all.

           Removing the trigger is the reliable half: this runs when the custom
           element upgrades, which is before DOMContentLoaded, so Ajaxinate
           finds nothing to bind when it initialises. destroy() covers the case
           where it has already bound.

           Scrolling still loads more, just not through Ajaxinate: populate()
           pages through the published-date ordering itself, from the feed it
           has already fetched. */
        document
          .querySelectorAll('.infinite_next, #pagination-wrapper, loadmore-products')
          .forEach((el) => el.remove());
        try {
          window.ajaxinateInstance?.destroy?.();
        } catch (error) {
          /* Ajaxinate throws if it was never bound; nothing to clean up. */
        }

        /* Rescue the theme's stylesheets BEFORE emptying the grid.

           card-product-var.liquid emits its asset <link> tags in a header at
           the top of the snippet, so on a collection page they are rendered
           INSIDE the first <li> - measured on /collections/allshoes: six of
           them, among-them component-card-fix.css, which carries every card
           rule and appears nowhere in <head>.

           Removing a <link> element removes its stylesheet from the document.
           So replaceChildren() was silently stripping all card styling, and
           the replacement cards rendered unstyled - reported as "loads the
           right products but not with the style it should have".

           Moving them into <head> keeps them applied and puts them somewhere
           nothing else will clear. Already-fetched, so no second download. */
        grid.querySelectorAll('link[rel="stylesheet"], style').forEach((node) => {
          document.head.appendChild(node);
        });

        const status = NewArrivals.buildStatus(
          this.dataset.loadingText || '',
          this.dataset.errorText || ''
        );
        grid.replaceChildren();
        /* Appended to <body>, not next to the grid - see populate() for why
           a transformed ancestor would otherwise break `position: fixed`. */
        document.body.appendChild(status);

        /* The grid is now empty, so it is safe to reveal. The inline script in
           main-collection-product-grid.liquid hid it during parsing precisely
           so the server's ordering never painted; releasing it here rather
           than after the cards land means the shopper sees an empty grid with
           its loading line, not a blank screen. */
        document.documentElement.classList.remove('published-sort-pending');

        await NewArrivals.populate({
          grid,
          status,
          handle,
          /* Products per LOAD, not in total. */
          pageSize: parseInt(this.dataset.count, 10) || 24,
          hideSoldOut: this.dataset.hideSoldOut === 'true',
          /* The collection grid is styled for this class - see harvestCards. */
          itemClass: 'grid__item',
        });
      }
    }
  );
  }
})();
